import { ref, computed } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { useCompareUrlState, ERouterMode } from '@/composables/use-compare-url-state';
import { useCompareInit } from '@/composables/use-compare-init';
import { useCompareWording } from '@/composables/use-compare-wording';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import {
    GetCompareText,
    type ComparePatentInfo,
} from '@/infrastructure/api-functions/core-search-api/core-search-api';
import _ from 'lodash';

export interface CompareTextMap {
    text: string;
    focus: boolean;
    delete: boolean;
    match: boolean;
    edit: boolean;
    partId?: string;
}

export const useCompareSource = createGlobalState(() => {
    const {
        rightId,
        compareId,
        isSourceCompareText,
        isSourceEditPatent,
        isSourceEditText,
        isSourceCustomText,

        changeCompareMode,
        prevCompareMode,
    } = useCompareUrlState();

    const {
        isSame,
        matchTextList,
        compareInit,
        resetCompareInitState,
        performFakeComparison,
    } = useCompareInit();

    const { resetCompareWordingState } = useCompareWording();


    // 语义检索->文本模式下loading直接为true
    const isLoading = ref(isSourceCompareText.value);


    // 显示在文本对比页面中的文本list
    const compareTechTextList = ref<CompareTextMap[]>([]);
    // 用作编辑使用，避免直接修改原数组
    const compareEditTextList = ref<CompareTextMap[]>([]);

    // 传给compare init的文本列表
    const compareResultTextList = computed(() => {
        const list = _.map(compareEditTextList.value, item => item.text);
        return list.filter(item => item.trim());
    });

    const getCompareTechText = async () => {
        isLoading.value = true;
        let textList:string[] = [];
        // 只有第一次请求时，需要调取接口
        if (compareResultTextList.value.length) {
            textList = _.cloneDeep(compareResultTextList.value);
        } else {
            const service = injectService<GetCompareText>(GetCompareText);
            const compareTextData = await service.getCompareText({
                compare_id: compareId.value,
            });
            textList = compareTextData.tech_text_list || [];
        }
        compareTechTextList.value = _.map(textList, text => {
            const matchItem = _.find(matchTextList.value, item => item.text === text);
            return {
                text,
                focus: false,
                delete: false,
                edit: false,
                match: !!matchItem,
                partId: matchItem?.partId,
            };
        });
        compareEditTextList.value = _.cloneDeep(compareTechTextList.value);
        isLoading.value = false;
    };

    const comparePatentInfo = ref<ComparePatentInfo>({} as ComparePatentInfo);

    const onCompareCancel = () => {
        changeCompareMode(prevCompareMode.value);
    };

    const isTextModeSubmit = async () => {
        isLoading.value = true;
        await compareInit({
            compare_id: compareId.value,
            target_patent_id: rightId.value,
            tech_text_list: compareResultTextList.value,
        });
        await getCompareTechText();
        if (!isSourceCustomText.value) {
            changeCompareMode(ERouterMode.TEXT_ID);
        }

        isLoading.value = false;
    };

    const isPatentModeSubmit = async () => {
        changeCompareMode(comparePatentInfo.value.PATENT_ID, 'clms');
        if (isSame.value)  {
            await performFakeComparison();
            return;
        }
        await compareInit({
            compare_id: compareId.value,
            target_patent_id: rightId.value,
            src_patent_id: comparePatentInfo.value.PATENT_ID,
        });
    };
    const onCompareSubmit = async () => {
        resetCompareInitState();
        resetCompareWordingState();
        if (isSourceEditPatent.value) {
            await isPatentModeSubmit();
        } else if(isSourceEditText.value) {
            await isTextModeSubmit();
        }
    };

    const selectPatent = async (patentInfo:ComparePatentInfo) => {
        comparePatentInfo.value = patentInfo;
        await onCompareSubmit();
    };

    return {
        isLoading,
        compareTechTextList,
        compareEditTextList,
        compareResultTextList,
        comparePatentInfo,

        getCompareTechText,
        selectPatent,
        onCompareCancel,
        onCompareSubmit,
        isTextModeSubmit,
        isPatentModeSubmit,
    };
});
